import { useMutation } from '@tanstack/react-query';
import { useAppContext } from 'src/contexts/AppContext';
import { ErrorTypes, useClientLogContext } from 'src/contexts/ClientLogContext';
import { AccountingEventClient, Feature } from 'src/WebApiController';

import { useUserHasFeature } from '../useUserHasFeature';

/**
 * Fetches a preview CSV for a specific accounting entity and stream (last hour window).
 * Dispatches to the correct endpoint based on stream.
 * Uses useMutation since this is triggered by an explicit user action (button click).
 */
export function useAccountingEventPreview(entityId: number, stream: string) {
  const { activeAccountWebClientConfig } = useAppContext();
  const { trackError } = useClientLogContext();

  const hasAccountingAuditExportFeature = useUserHasFeature(
    Feature.AccountingAuditExport
  );
  const hasAccountingFeedUIFeature = useUserHasFeature(
    Feature.AccountingFeedUI
  );
  const hasFeature =
    hasAccountingAuditExportFeature || hasAccountingFeedUIFeature;

  return useMutation({
    mutationFn: async () => {
      if (!hasFeature) return null;
      const client = new AccountingEventClient(activeAccountWebClientConfig);
      switch (stream) {
        case 'invoice':
          return await client.getInvoicePreview(entityId);
        case 'invoice_payment':
          return await client.getInvoicePaymentPreview(entityId);
        case 'bills':
          return await client.getBillsPreview(entityId);
        case 'bills_payment':
          return await client.getBillsPaymentPreview(entityId);
        default:
          return null;
      }
    },
    onError: (err: unknown) => {
      trackError(
        `AccountingEventClient.getPreview.${stream}`,
        err as ErrorTypes,
        { entityId, stream }
      );
    },
  });
}
